import { trpc } from '@/main'
import { createFileRoute, Outlet, useSearch } from '@tanstack/react-router'
import { Suspense, useEffect, useState } from 'react'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { jobIntegrantsSource } from '@/hooks/use-job'
import { reportSearchDeps, reportQueryOptions } from '@/hooks/use-report'
import { deriveProfiles } from '@/hooks/use-root-search'
import { JobDialog } from './-components/jobs/job-dialog'

/**
 * Layout for a selected job (`/jobs/$jobId/*`). This route renders real content (the incomplete-job
 * auto-open + the animated tab Outlet), so per AGENTS.md it OWNS the jobId-keyed prefetches that its
 * subtree reads — the persistent chrome (getJob + integrants list) and the report data shared by all
 * three tab children. The report/board/distribution leaves no longer duplicate these; they keep only
 * their own tab-specific queries (board/distribution: the integrant indicators).
 *
 * The `AnimatedOutlet` now lives in the parent `jobs.tsx` (wrapping the `<Outlet/>` that renders this
 * layout) so the enter animation plays once when the report panel first appears and never re-animates on
 * tab/job switches; this layout renders a plain `<Outlet/>` for its tab children. The auto-open's dismiss
 * state still persists across tab AND job switches because this chrome-owning layout stays mounted.
 */
export const Route = createFileRoute('/_app/jobs/$jobId')({
  // EXACTLY the search this layout's context reads. `reportSearchDeps` already covers `first`/`second`/
  // `type` (the report keys); `o`/`od` are the list's own sort. Anything else changing must not re-run it.
  loaderDeps: ({ search }) => ({ ...reportSearchDeps(search), o: search.o, od: search.od, panelQ: search.panelQ }),
  context: ({ deps, params }) => ({
    // The detail every pane on this screen shows. The chrome ABOVE this match (breadcrumb, screen
    // title, the integrants panel) reads THIS field off the match rather than rebuilding the key.
    jobQueryOptions: trpc.jobs.getJob.queryOptions({ jobId: Number(params.jobId) }),
    // The integrants list keyed on the pane's OWN search param. The panel that renders it sits in the
    // parent `jobs.tsx`, above this match, so it reads THIS field off the match — its search box writes
    // `panelQ` (not `q`, which the rail beside it owns), so there is one key, not a searched variant.
    jobIntegrantsInfiniteQueryOptions: trpc.jobs.getJobIntegrants.infiniteQueryOptions(
      {
        jobId: Number(params.jobId),
        type: deps.type,
        o: deps.o,
        od: deps.od,
        q: deps.panelQ,
        employeesOnly: true,
        source: jobIntegrantsSource(deriveProfiles(deps, params, false).first),
      },
      { getNextPageParam: (lastPage) => lastPage.nextCursor },
    ),
    reportQueryOptions: reportQueryOptions(deps, params),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.jobQueryOptions).catch(noop)
    context.queryClient.infiniteQuery(context.jobIntegrantsInfiniteQueryOptions).catch(noop)
    if (context.reportQueryOptions) context.queryClient.query(context.reportQueryOptions).catch(noop)
  },
  component: RouteComponent,
  // No pendingComponent: this passthrough layout just renders <AnimatedOutlet/>. The data-cold case is
  // covered by the parent jobs.tsx (<Suspense fallback={ReportPanelSkeleton}>), and each tab leaf
  // (report/board/distribution) owns its own tab-specific skeleton — a generic one here would mismatch.
})

function RouteComponent() {
  // `?describeJob=` is an explicit request for the describe wizard (the public API's "link para
  // describir el puesto" lands here). The completion nag exists to push an owner TOWARDS describing an
  // incomplete job, so when the URL already asks for it the nag is redundant — and it opens second,
  // stacking a modal on top of the wizard the link was for.
  const describeRequested = useSearch({ from: '__root__', select: (s) => s.describeJob != null })
  return (
    <>
      {!describeRequested && (
        <Suspense fallback={null}>
          <IncompleteJobAutoOpen />
        </Suspense>
      )}
      <Outlet />
    </>
  )
}

/**
 * Auto-opens the completion dialog for an incomplete job the viewer owns. Reads `getJob` via suspense
 * (loader-prefetched above), inside its own <Suspense fallback={null}> so a cold read never blanks the
 * tab Outlet. Dismiss state is local to this layout — it persists across tab switches (the layout stays
 * mounted) and across job switches (same route, param change), and resets only when the selection is
 * cleared (navigating back to the `/jobs` list), which is the intended session scope.
 */
function IncompleteJobAutoOpen() {
  const { data: job } = useSuspenseQuery(Route.useRouteContext().jobQueryOptions)
  const [dismissed, setDismissed] = useState<Set<number>>(() => new Set())

  // Once a job is complete it can never auto-open, so drop any stale dismiss flag (matters only if it
  // later becomes incomplete again).
  useEffect(() => {
    if (job.fullyRegistered) {
      // oxlint-disable-next-line react/set-state-in-effect -- external data sync: drops a stale dismiss flag when the job's fetched data flips to fullyRegistered
      setDismissed((prev) => {
        if (!prev.has(job.id)) return prev
        const next = new Set(prev)
        next.delete(job.id)
        return next
      })
    }
  }, [job])

  const shouldOpen = job.viewerRole === 'owner' && !job.fullyRegistered && !dismissed.has(job.id)

  // Mount the dialog on first open and keep it mounted, driving visibility through `open` — a bare
  // `if (!shouldOpen) return null` would unmount it on dismiss and skip the exit animation.
  //
  // Latched DURING render rather than in an effect: an effect needs a second commit before `mounted` is
  // true, so the dialog appeared one paint late on the very first open. This mounts it in the same commit
  // that first computes `shouldOpen`.
  const [mounted, setMounted] = useState(false)
  if (shouldOpen && !mounted) setMounted(true)

  if (!mounted) return null

  return (
    <JobDialog
      jobId={job.id}
      open={shouldOpen}
      onOpenChange={(open) => {
        if (!open) setDismissed((prev) => new Set(prev).add(job.id))
      }}
    >
      <span />
    </JobDialog>
  )
}
